import { useDispatch, useSelector } from "react-redux";
import PageContainer from "../components/PageContainer";
import PageTitle from "../components/PageTitle";
import { useNavigate } from "react-router-dom";
import { getUnlogged, fetchUserRequests } from "../redux/userSlice";
import { useCallback, useEffect, useState } from "react";
import { useRefreshHandler } from "../components/pullToRefresh/useRefreshHandler";
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import UserRequestCard from "../components/booking/UserRequestCard";
import CustomHr from "../components/CustomHr";
import Loader from '../components/Loader';
import Error from '../components/Error';
import { setBacklink } from "../redux/backlinkSlice.js";
import { useTranslation } from 'react-i18next';
import AccordionWrapper from "../components/AccordionWrapper";
import { CreditCard, Truck, User, WalletCards } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

const ProfilePage = () => {

    const { t } = useTranslation();
    const { id, user, level, requests, requestsStatus, requestsError, favourites, isUserPurchaseAvailable, isUserShopOrderAvailable } = useSelector(state => state.user);
    const token = localStorage.getItem('ticino-wild-token');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const setLogout = () => {
        localStorage.removeItem('ticino-wild-token');
        dispatch(getUnlogged());
        navigate("/");
    }

    // Recupero l'email in backend
    const [email, setEmail] = useState(null);
    const [emailFetchStatus, setEmailFetchStatus] = useState('idle');
    const [emailError, setEmailError] = useState(null);
    const getEmail = async () => {
        setEmailFetchStatus('loading');
        const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/get-email/${id}`;
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        const options = { method: 'GET', headers };
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setEmail(result.payload);
                setEmailFetchStatus('succeeded');
            } else {
                const error = await response.json();
                throw error;
            }
        } catch (error) {
            setEmailError(error.message || "Errore nel riceve l'indirizzo email.");
            setEmailFetchStatus('failed');
        }
    }
    useEffect(() => {
        if (id) getEmail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    // Mando favourites in backend per riceverne le attività corrispondenti
    const [, setFavouritesData] = useState([]);
    const [, setIsFavouritesLoading] = useState(false);
    const [, setFavouritesError] = useState(null);
    useEffect(() => {
        if (favourites) {
            if (favourites.length > 0) {
                favouritesDataFetch();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favourites])

    const favouritesDataFetch = async () => {
        setIsFavouritesLoading(true);
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-user-activities-by-id-array`;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const options = {
                method: 'POST',
                headers,
                body: JSON.stringify({ favouritesIdArray: favourites.map(f => f.activityId) })
            };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setFavouritesData(result.payload);
                setIsFavouritesLoading(false);
            } else {
                const error = await response.json();
                throw error;
            }
        } catch (error) {
            console.error(error.message || "Errore durante la richiesta Favourites.");
            setFavouritesError(error);
            setIsFavouritesLoading(false);
        }
    }

    // Verifico se sono impostati i dati d'acquisto dell'utente. In caso positivo eseguo la chiamata per riceverli
    const [purchaseData, setPurchaseData] = useState(null);
    const [, setPurchaseDataFetchStatus] = useState('idle');
    const [, setPurchaseDataError] = useState(null);
    const getPurchaseData = async () => {
        setPurchaseDataFetchStatus('loading');
        const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/get-purchase-data/${id}`;
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        const options = { method: 'GET', headers };
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setPurchaseData(result.payload);
                setPurchaseDataFetchStatus('succeeded');
            } else {
                const error = await response.json();
                throw error
            }
        } catch (error) {
            setPurchaseDataError(error.message || "Errore nel ricevere i dati d'acquisto.");
            setPurchaseDataFetchStatus('failed');
        }
    }
    useEffect(() => {
        if (isUserPurchaseAvailable) getPurchaseData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserPurchaseAvailable])
    // Gestisco click sul button di impostazione dati d'acquisto utente
    const handleSetDataClick = () => {
        dispatch(setBacklink("/my-profile"));
        navigate("/set-user-data");
    }

    // Verifico se sono impostati i dati di spedizione dell'utente. In caso positivo eseguo la chiamata per riceverli
    const [shippingData, setShippingData] = useState(null);
    const [, setShippingDataFetchStatus] = useState('idle');
    const [, setShippingDataError] = useState(null);
    const getShippingData = async () => {
        setShippingDataFetchStatus('loading');
        const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/get-shipping-data/${id}`;
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        const options = { method: 'GET', headers };
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setShippingData(result.payload);
                setShippingDataFetchStatus('succeeded');
            } else {
                const error = await response.json();
                throw error
            }
        } catch (error) {
            setShippingDataError(error.message || "Errore nel ricevere i dati di spedizione.");
            setShippingDataFetchStatus('failed');
        }
    }
    useEffect(() => {
        if (isUserShopOrderAvailable) getShippingData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserShopOrderAvailable])
    // Gestisco click sul button di impostazione dati di spedizione utente
    const handleSetShippingDataClick = () => {
        dispatch(setBacklink("/my-profile"));
        navigate("/set-shipping-data");
    }

    // Richieste dell'utente: rilette fresche dal DB (con titoli già joinati) all'atterraggio sul profilo.
    // Così lo stato "accepted" (dopo l'ok del partner) è visibile senza refresh completo dell'app.
    // Solo le richieste "card-ready" (già arricchite dal thunk) vengono renderizzate: quelle grezze
    // seminate al boot da login/refresh non hanno requestId/title e restano fuori finché il thunk non risponde.
    const cardRequests = (requests || []).filter(r => r && r.requestId);
    useEffect(() => {
        dispatch(fetchUserRequests());
    }, [dispatch]);
    // Pull-to-refresh (nativo): ricarica le richieste dell'utente (il thunk restituisce una promise → spinner fino al termine).
    useRefreshHandler(useCallback(() => dispatch(fetchUserRequests()), [dispatch]));
    // Nativo: rilancia il fetch quando l'app torna in primo piano (es. partner accetta mentre l'app è in background).
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        let listener;
        (async () => {
            listener = await CapApp.addListener('resume', () => {
                dispatch(fetchUserRequests());
            });
        })();
        return () => { if (listener) listener.remove(); };
    }, [dispatch]);

    // CANCELLAZIONE ACCOUNT
    const [pendingDeletionAt, setPendingDeletionAt] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deletionRequestStatus, setDeletionRequestStatus] = useState('idle'); // idle | loading | succeeded | failed
    const [deletionRequestError, setDeletionRequestError] = useState(null);
    const [deletionCancelStatus, setDeletionCancelStatus] = useState('idle');

    const fetchDeletionStatus = async () => {
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/deletion-status`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setPendingDeletionAt(result.payload?.pendingDeletionAt || null);
            }
        } catch (error) {
            // silente: il banner non si mostra, l'utente può comunque richiedere
        }
    };
    useEffect(() => {
        if (id) fetchDeletionStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleRequestDeletion = async () => {
        setDeletionRequestStatus('loading');
        setDeletionRequestError(null);
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/request-account-deletion`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            const result = await response.json();
            if (response.ok) {
                setDeletionRequestStatus('succeeded');
                if (result.payload?.pendingDeletionAt) {
                    setPendingDeletionAt(result.payload.pendingDeletionAt);
                }
            } else {
                setDeletionRequestError(result.message || 'Errore durante la richiesta.');
                setDeletionRequestStatus('failed');
            }
        } catch (error) {
            setDeletionRequestError(error.message);
            setDeletionRequestStatus('failed');
        }
    };

    const handleCancelDeletion = async () => {
        setDeletionCancelStatus('loading');
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/cancel-account-deletion`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                setPendingDeletionAt(null);
                setDeletionCancelStatus('succeeded');
            } else {
                setDeletionCancelStatus('failed');
            }
        } catch (error) {
            setDeletionCancelStatus('failed');
        }
    };

    const closeDeleteDialog = () => {
        setShowDeleteDialog(false);
        setDeletionRequestStatus('idle');
        setDeletionRequestError(null);
    };

    return (
        <PageContainer desktopItems="center">
            <PageTitle backLink="/" title={t('ProfilePage.title')} />


            {/* Accordion group */}
            <div className="flex flex-col gap-4 w-full max-w-4xl">
                {/* Basic info */}
                <AccordionWrapper
                    title={t('ProfilePage.userInfo')}
                    icon={<User color="#225424" />}
                >
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col lg:flex-row gap-1">
                            <label htmlFor="id" className="text-sm text-[#225424]">{t('ProfilePage.userId')}</label>
                            <div>{id}</div>
                        </div>
                        <div className="flex flex-col lg:flex-row gap-1">
                            <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.userName')}</label>
                            <div>{user}</div>
                        </div>
                        <div className="flex flex-col lg:flex-row lg:items-center gap-1">
                            <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.email')}</label>
                            {
                                emailFetchStatus === 'loading' &&
                                <Loader />
                            }
                            {
                                emailFetchStatus === 'failed' &&
                                <Error text={emailError} message="Ricarica la pagina." />
                            }
                            {
                                emailFetchStatus === 'succeeded' &&
                                <div>{email}</div>
                            }
                        </div>
                    </div>
                </AccordionWrapper>

                {/* Dati d'acquisto utente */}
                <AccordionWrapper
                    title={t('ProfilePage.yourPurchaseData')}
                    icon={<CreditCard color="#225424" />}
                >
                    <div className="flex flex-col">
                        {
                            isUserPurchaseAvailable &&
                            <div className="flex flex-col gap-4">
                                <h4 className="text-neutral-400">{t('ProfilePage.yourPurchaseData')}</h4>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="id" className="text-sm text-[#225424]">{t('ProfilePage.firstName')}</label>
                                    <div>{purchaseData?.firstName}</div>
                                </div>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.lastName')}</label>
                                    <div>{purchaseData?.lastName}</div>
                                </div>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.phoneNumber')}</label>
                                    <div>{purchaseData?.phoneNumber}</div>
                                </div>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.birthdate')}</label>
                                    <div>{purchaseData?.birthdate}</div>
                                </div>
                            </div>
                        }

                        {
                            !isUserPurchaseAvailable &&
                            <div className="flex flex-col gap-4">
                                <h3>{t('ProfilePage.purchaseDataNotSetYet')}</h3>
                                <p>{t('ProfilePage.setYourData')}</p>
                            </div>
                        }
                        <button className='primary mt-4 w-fit' onClick={handleSetDataClick}>
                            {isUserPurchaseAvailable ? `${t('ProfilePage.editDataButton')}` : `${t('ProfilePage.addDataButton')}`}
                        </button>
                    </div>
                </AccordionWrapper>

                {/* Dati di spedizione utente */}
                <AccordionWrapper
                    title={t('ProfilePage.yourShippingData')}
                    icon={<Truck color="#225424" />}
                >
                    <div className="flex flex-col">
                        {
                            isUserShopOrderAvailable &&
                            <div className="flex flex-col gap-4">
                                <h4 className="text-neutral-400">{t('ProfilePage.yourShippingData')}</h4>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="id" className="text-sm text-[#225424]">{t('ProfilePage.country')}</label>
                                    <div>{shippingData?.country}</div>
                                </div>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.city')}</label>
                                    <div>{shippingData?.city}</div>
                                </div>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.postalCode')}</label>
                                    <div>{shippingData?.postalCode}</div>
                                </div>
                                <div className="flex flex-col lg:flex-row gap-1">
                                    <label htmlFor="user" className="text-sm text-[#225424]">{t('ProfilePage.address')}</label>
                                    <div>{shippingData?.address}</div>
                                </div>
                            </div>
                        }

                        {
                            !isUserShopOrderAvailable &&
                            <div className="flex flex-col gap-4">
                                <h3>{t('ProfilePage.shippingDataNotSetYet')}</h3>
                                <p>{t('ProfilePage.setYourShippingData')}</p>
                            </div>
                        }
                        <button className='primary mt-4 w-fit' onClick={handleSetShippingDataClick}>
                            {isUserShopOrderAvailable ? `${t('ProfilePage.editShippingDataButton')}` : `${t('ProfilePage.addShippingDataButton')}`}
                        </button>
                    </div>
                </AccordionWrapper>

                {/* Le tue attività prenotate */}
                <AccordionWrapper
                    title={t('ProfilePage.yourRequests')}
                    icon={<WalletCards color="#225424" />}
                >
                    <div className="flex flex-col gap-2">
                        {
                            requestsStatus === 'loading' && cardRequests.length === 0 &&
                            <Loader />
                        }
                        {
                            requestsStatus === 'succeeded' && cardRequests.length === 0 &&
                            <p className="text-neutral-500 text-sm">{t('ProfilePage.noRequests')}</p>
                        }
                        {
                            requestsStatus === 'failed' && cardRequests.length === 0 &&
                            <p className="text-red-600 text-sm">{requestsError || t('ProfilePage.noRequests')}</p>
                        }
                        {
                            cardRequests.map((element, index) => {
                                return (
                                    <UserRequestCard
                                        key={element.requestId || index}
                                        requestData={element}
                                        allowCancel
                                        allowPayAccepted
                                        allowAdminCancelAccepted={level >= 2}
                                        syncUserRequestsInReduxOnAdminCancel
                                    />
                                )
                            })
                        }
                    </div>
                </AccordionWrapper>
            </div>


            {/* Le tue attività preferite */}
            {/* <h3 className="mt-8">{t('ProfilePage.yourFavouriteActivities')}</h3>
            <CustomHr /> */}

            {/* CARD CONTAINER */}
            {/* <div className="flex flex-col py-4">
                {
                    favouritesData && !isFavouritesLoading && !favouritesError &&
                    <CardContainer>
                        {
                            favouritesData.length > 0 && favouritesData.map((element, index) => {
                                return <ActivityCard key={index} activityData={element} />
                            })
                        }
                    </CardContainer>
                }
                {
                    isFavouritesLoading &&
                    <Loader />
                }
                {
                    favouritesError && !isFavouritesLoading &&
                    <Error />
                }

            </div> */}



            {/* Logout*/}
            <h4 className="mt-8">{t('ProfilePage.logout')}</h4>
            <CustomHr />

            <button className="secondary w-fit" onClick={setLogout}>{t('ProfilePage.getLogout')}</button>

            {/* Cancellazione account */}
            <h4 className="mt-8 text-red-700">{t('AccountDeletion.title')}</h4>
            <CustomHr />

            {pendingDeletionAt ? (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg flex flex-col gap-3 max-w-2xl">
                    <p className="text-sm text-neutral-800">
                        {t('AccountDeletion.scheduledForDate', {
                            date: new Date(pendingDeletionAt).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
                        })}
                    </p>
                    <p className="text-xs text-neutral-600">
                        {t('AccountDeletion.scheduledHint')}
                    </p>
                    <button
                        className="primary w-fit disabled:opacity-50"
                        onClick={handleCancelDeletion}
                        disabled={deletionCancelStatus === 'loading'}
                    >
                        {deletionCancelStatus === 'loading' ? t('AccountDeletion.cancelLoading') : t('AccountDeletion.cancelButton')}
                    </button>
                    {deletionCancelStatus === 'failed' && (
                        <p className="text-xs text-red-700">{t('AccountDeletion.cancelError')}</p>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-3 max-w-2xl">
                    <p className="text-sm text-neutral-700">
                        {t('AccountDeletion.description')}
                    </p>
                    {deletionRequestStatus === 'succeeded' ? (
                        <p className="text-sm text-green-700">
                            {t('AccountDeletion.emailSentSuccess')}
                        </p>
                    ) : (
                        <button
                            className="bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-xl w-fit"
                            onClick={() => setShowDeleteDialog(true)}
                        >
                            {t('AccountDeletion.deleteButton')}
                        </button>
                    )}
                </div>
            )}

            <ConfirmDialog
                isOpen={showDeleteDialog}
                title={t('AccountDeletion.confirmDialog.title')}
                message={t('AccountDeletion.confirmDialog.message')}
                confirmLabel={t('AccountDeletion.confirmDialog.confirm')}
                cancelLabel={t('AccountDeletion.confirmDialog.cancel')}
                confirmingLabel={t('AccountDeletion.confirmDialog.confirming')}
                onConfirm={async () => {
                    await handleRequestDeletion();
                    setShowDeleteDialog(false);
                }}
                onCancel={closeDeleteDialog}
                isConfirming={deletionRequestStatus === 'loading'}
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-xl"
                error={deletionRequestError}
            />
        </PageContainer>
    )
}

export default ProfilePage;